import { createContext, use, useReducer } from 'react';

interface MicrositeState {
	sections: MicrositeSection[];
	gallery: boolean;
}

export interface MicrositeSection {
	id: string;
	name: string;
	navigate: () => void;
}

interface MicrositeContext {
	state: MicrositeState;
	dispatch: (action: MicrositeStateAction) => void;
}

const context = createContext<MicrositeContext | null>(null);

export enum MicrositeStateActionType {
	SECTION_REGISTER = 'SECTION_REGISTER',
	SECTION_UNREGISTER = 'SECTION_UNREGISTER',
	GALLERY_OPEN = 'GALLERY_OPEN',
	GALLERY_CLOSE = 'GALLERY_CLOSE',
}

type MicrositeStateAction =
	| {
			type: MicrositeStateActionType.SECTION_REGISTER;
			id: string;
			name: string;
			navigate: () => void;
	  }
	| {
			type: MicrositeStateActionType.SECTION_UNREGISTER;
			id: string;
	  }
	| {
			type: MicrositeStateActionType.GALLERY_OPEN;
	  }
	| {
			type: MicrositeStateActionType.GALLERY_CLOSE;
	  };

function reducer(state: MicrositeState, action: MicrositeStateAction) {
	if (action.type === MicrositeStateActionType.SECTION_REGISTER) {
		const sections = state.sections.filter((section) => section.id !== action.id);

		return {
			...state,
			sections: [...sections, { id: action.id, name: action.name, navigate: action.navigate }],
		};
	}

	if (action.type === MicrositeStateActionType.SECTION_UNREGISTER) {
		return {
			...state,
			sections: state.sections.filter((s) => s.id !== action.id),
		};
	}

	if (action.type === MicrositeStateActionType.GALLERY_OPEN) {
		return {
			...state,
			gallery: true,
		};
	}

	if (action.type === MicrositeStateActionType.GALLERY_CLOSE) {
		return {
			...state,
			gallery: false,
		};
	}

	return state;
}

export function MicrositeStateProvider({ children, initial }: { children: React.ReactNode; initial?: Partial<MicrositeState> }) {
	const [state, dispatch] = useReducer(reducer, {
		sections: [],
		gallery: false,
		...(initial ?? {}),
	});

	return <context.Provider value={{ state, dispatch }}>{children}</context.Provider>;
}

export default function useMicrositeState() {
	const ctx = use(context);

	if (!ctx) {
		throw new Error('useMicrositeState must be used within a MicrositeStateProvider');
	}

	return ctx;
}
